// Whether a team is ready to play, and what it is short of.
//
// Advisory, never blocking. Nobody is turned away mid-sign-up because of who is
// already on a team — the club sees what is short and sorts it out while
// building the schedule. So this only ever describes; it never refuses.
//
// The rule for a coed league: the team needs the league's format in players
// (4 for 4s), and at least one of them female. One is a MINIMUM, not a quota —
// two women and two men is a valid coed 4s team, and so is four women. Slots
// above the format, up to rosterMax, are open to anyone.
//
// Men's, women's and single-gender junior leagues have no mix requirement:
// everyone in them is the same gender by definition.

import { leagueTypeOf } from './leagueTypes';

/** Leagues that mix genders, and therefore have something to require. */
export const isCoedType = (t) => t === 'coed' || t === 'junior_coed';

/** How many women a team in this league must have. 0 when it cannot apply. */
export function minFemale(league) {
  return isCoedType(leagueTypeOf(league)) ? 1 : 0;
}

/**
 * What a team has, needs, and may still take.
 *
 * `members` is whatever the caller has to hand — registrations, or the trimmed
 * rows the roster callable returns — as long as each carries a `gender`.
 *
 * unknownGender is reported separately rather than counted either way: someone
 * who registered before the question was asked, or who declined it, is not
 * evidence for or against the rule, and a count that quietly assumed one would
 * tell the club a team was fine when nobody knows.
 */
export function rosterStatus(league, members = []) {
  const format = Number(league?.format) || 0;
  const rosterMax = Number(league?.rosterMax) || format || 0;
  const list = Array.isArray(members) ? members : [];

  const count = list.length;
  const females = list.filter(m => m?.gender === 'Female').length;
  const unknownGender = list.filter(m => !String(m?.gender || '').trim()).length;

  const needFemale = minFemale(league);
  const shortPlayers = Math.max(0, format - count);
  const shortFemale = Math.max(0, needFemale - females);

  return {
    count,
    females,
    unknownGender,
    format,
    rosterMax,
    // Room measured against rosterMax, which is what actually caps a roster;
    // format is the minimum to field a team, not the ceiling.
    spotsLeft: rosterMax > 0 ? Math.max(0, rosterMax - count) : null,
    shortPlayers,
    shortFemale,
    needFemale,
    complete: shortPlayers === 0 && shortFemale === 0,
  };
}

/**
 * One line saying what the team is short of, or '' when it is ready.
 *
 * Deliberately a sentence rather than a code: it is read by a captain who has
 * to go and do something about it, not by a machine.
 */
export function rosterShortfall(league, members = []) {
  const s = rosterStatus(league, members);
  const parts = [];
  if (s.shortPlayers > 0) {
    parts.push(`${s.shortPlayers} more player${s.shortPlayers === 1 ? '' : 's'}`);
  }
  if (s.shortFemale > 0) {
    parts.push(`${s.shortFemale} more female player${s.shortFemale === 1 ? '' : 's'}`);
  }
  if (!parts.length) return '';
  // "3 more players, 1 of them female" reads better than listing both as
  // separate needs, since the second is satisfied by one of the first.
  if (s.shortPlayers > 0 && s.shortFemale > 0) {
    return `Needs ${s.shortPlayers} more player${s.shortPlayers === 1 ? '' : 's'}`
      + `, ${s.shortFemale === 1 ? 'at least 1 female' : `at least ${s.shortFemale} female`}`;
  }
  return `Needs ${parts[0]}`;
}
